// src/lib/use-notif-counters.js — Compteurs de notification au niveau App
// (badges des menus parents, ADR-269).
//
// POURQUOI ici et pas dans MessagesPanel : le badge parent « Communication »
// doit vivre SANS que la section soit montée (l'admin peut rester des heures
// dans Calendrier). Le hook remonte donc les deux compteurs au niveau App et
// MessagesPanel les REÇOIT en prop (source unique — zéro double polling quand
// la section est ouverte).
//
// COÛT : mêmes requêtes count `head:true` que MessagesPanel historique, même
// cadence 60 s (aucune nouvelle table, aucun nouvel endpoint, payload nul).
// `enabled` = admin uniquement (les autres rôles n'ont pas ces badges et la
// RLS ne leur ouvre pas ces tables).

import { useState, useEffect, useCallback } from "react"
import { supabase } from "../supabase"

export const NOTIF_POLL_MS = 60_000

// Messages entrants non lus (pill « Message » de Communication).
export async function fetchUnreadInboxCount() {
  const { count, error } = await supabase
    .from("inbox_messages")
    .select("id", { count: "exact", head: true })
    .eq("direction", "inbound")
    .is("read_at", null)
  if (error) { console.error("[notif] unread inbox count failed", error); return 0 }
  return count || 0
}

// Demandes de réservation directe en attente (pill « Demandes »).
export async function fetchPendingRequestsCount() {
  const { count, error } = await supabase
    .from("direct_booking_requests")
    .select("id", { count: "exact", head: true })
    .eq("statut", "pending")
  if (error) { console.error("[notif] pending requests count failed", error); return 0 }
  return count || 0
}

/**
 * Hook App-level : tient les 2 compteurs Communication + leur rafraîchissement
 * (montage + toutes les NOTIF_POLL_MS). Expose aussi les setters/refreshers
 * que MessagesPanel branchait sur ses enfants (InboxPanel.onUnreadChange,
 * BookingRequestsView.onPendingChange) pour une mise à jour immédiate au clic.
 * @param {boolean} enabled — admin uniquement ; false = inerte (0, 0).
 */
export function useNotifCounters(enabled) {
  const [unreadInbox, setUnreadInbox] = useState(0)
  const [pendingRequests, setPendingRequests] = useState(0)

  const refreshUnread = useCallback(async () => {
    if (!enabled) return
    setUnreadInbox(await fetchUnreadInboxCount())
  }, [enabled])

  const refreshPendingRequests = useCallback(async () => {
    if (!enabled) return
    setPendingRequests(await fetchPendingRequestsCount())
  }, [enabled])

  useEffect(() => {
    if (!enabled) return undefined
    refreshUnread()
    refreshPendingRequests()
    const id = setInterval(() => { refreshUnread(); refreshPendingRequests() }, NOTIF_POLL_MS)
    return () => clearInterval(id)
  }, [enabled, refreshUnread, refreshPendingRequests])

  return { unreadInbox, pendingRequests, refreshUnread, refreshPendingRequests, setPendingRequests }
}
